import type { NpcFaction, NpcHullTier } from "../types";
import {
  ROLE_POLYGON,
  NPC_POLYGON,
  ALIEN_ELLIPSE,
  FACTION_COLOR,
  PLAYER_COLOR,
  TIER_NUMERAL,
  type PolygonPoint
} from "./shipCanvasIcon";

// Blueprint View (design doc 16.10x, Don's direction) -- a top-down
// orthographic technical drawing for every hull in the game, reachable
// from the Captain's Handbook's Ships tab, the Trophy Room's Opponent
// grid, and a "More Info" button on the Expedition View's Target Bar.
//
// Deliberately NOT a new visual language: every blueprint is built from
// the exact same polygon/ellipse point tables shipCanvasIcon.ts already
// uses to draw the colored-in Expedition View silhouettes (ROLE_POLYGON/
// NPC_POLYGON/ALIEN_ELLIPSE), so a hull's blueprint and its in-flight
// icon can never drift apart -- one shape, two renderings. The Expedition
// View itself stays colored-in silhouettes; this is an alternate detail
// view, not a replacement (Don's explicit direction).
//
// The geometry annotation (hardpoint pods, mining beam, cargo bay,
// repair ports, sensor mast) is generic and DERIVED from each polygon's
// own vertices at render time -- not hand-placed per hull -- so adding a
// 14th hull later is a data-table entry, not a new drawing routine.

export type BlueprintFaction = NpcFaction | "Player";

export interface BlueprintLoadout {
  hardpoints: 0 | 1 | 2 | 3 | 4;
  miningBeam?: boolean;
  cargoBay?: boolean;
  repairPorts?: boolean;
  sensorMast?: boolean;
}

// ---- Loadout identity, one row per shape, grounded in real stats ------
// Digger's 0 hardpoints matches its real 0 DPS (starforge-player-ships.md);
// Wildcat's 4 matches "the most damage per tier" (ROLE_BLURBS); Logi's 0
// hardpoints + repair ports matches its dps<Line and its real remote-repair
// stat (encounters.json selfRemoteRepairHpPerS). The drawing agrees with
// the numbers because it is read off the same source data, not invented
// alongside it.
export const PLAYER_LOADOUT: Record<string, BlueprintLoadout> = {
  Digger: { hardpoints: 0, miningBeam: true, cargoBay: true },
  Packmule: { hardpoints: 1, cargoBay: true },
  Kestrel: { hardpoints: 1, sensorMast: true },
  Journeyman: { hardpoints: 2, miningBeam: true, cargoBay: true, sensorMast: true },
  Wildcat: { hardpoints: 4, sensorMast: true },
  Marauder: { hardpoints: 3, sensorMast: true }
};

export const NPC_LOADOUT: Record<string, BlueprintLoadout> = {
  Raider: { hardpoints: 2, sensorMast: true },
  Micro: { hardpoints: 1 },
  Leader: { hardpoints: 3, sensorMast: true },
  Line: { hardpoints: 2, sensorMast: true },
  Logi: { hardpoints: 0, repairPorts: true, sensorMast: true }
};

const PLAYER_BLURB: Record<string, string> = {
  Digger: "The miner. Largest ore hold in the game and the reason to fit a Mining Laser at all. Almost no weapons — it earns by never being shot at.",
  Packmule: "The hauler. Built around cargo volume for trade goods and mission containers, with enough hull to survive a bad jump.",
  Journeyman: "The generalist. Middling at everything, bad at nothing, and the least punishing hull to learn the game in.",
  Kestrel: "Fast and thin. Gets somewhere first and leaves before the damage lands. Rewards judgement and punishes a slow decision.",
  Marauder: "The brawler. The heaviest hull and shield in its tier, slow enough that a fight it starts is a fight it finishes.",
  Wildcat: "Guns over armour. The most damage per tier, with the least left over to absorb any of it coming back."
};

// Same dossier "role" copy as the Trophy Room (loreCodex.ts NPC_DOSSIERS),
// duplicated as plain strings rather than imported to avoid a dependency
// on lore text from a geometry module -- these are stable, already-locked
// lines, not content this module owns.
const NPC_BLURB: Record<string, string> = {
  Raider: "Pirate line unit — the crew that actually takes your cargo.",
  Micro: "Pirate fodder — the hull tier no legitimate yard builds.",
  Leader: "Pirate command unit — the reason a swarm holds together.",
  Line: "Chartered navy — the hull all three factions actually field.",
  Logi: "Chartered support — the ship that decides how long the fight lasts.",
  Duelist: "Alien contact — never in groups, never explained.",
  Elite: "Alien apex — the longest fight in ordinary space."
};

export interface BlueprintStat {
  label: string;
  value: string;
}

export interface BlueprintSpec {
  key: string; // Digger/.../Marauder, Raider/.../Logi, Duelist, Elite
  faction: BlueprintFaction;
  displayName: string; // "Kestrel II" / "Pirate Raider (T2)"
  tier: NpcHullTier;
  stats: BlueprintStat[];
  drawingNo: string; // e.g. "SF-KST-002"
}

const TIER_SCALE: Record<NpcHullTier, number> = { T1: 0.78, T2: 0.88, T3: 0.96, T4: 1.06 };

function rot(p: PolygonPoint): PolygonPoint {
  // Local frame convention (shipCanvasIcon.ts): nose at local +x. A
  // blueprint reads nose-up, like every other DOM ship icon in the game
  // (Trophy Room's own comment on this exact rotation) -- (x,y) -> (y,-x).
  return [p[1], -p[0]];
}

function svgEl(drawing: { clip: string; body: string }): string {
  return (
    '<svg viewBox="0 0 480 480" xmlns="http://www.w3.org/2000/svg">' +
    '<defs><filter id="bpGlow" x="-60%" y="-60%" width="220%" height="220%">' +
    '<feGaussianBlur stdDeviation="2" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>' +
    "</filter>" +
    '<pattern id="bpGrid" width="18" height="18" patternUnits="userSpaceOnUse">' +
    '<path d="M18,0 L0,0 0,18" fill="none" class="bp-grid"/></pattern>' +
    `<clipPath id="bpHullClip">${drawing.clip}</clipPath>` +
    "</defs>" +
    '<rect x="0" y="0" width="480" height="480" fill="url(#bpGrid)"/>' +
    drawing.body +
    "</svg>"
  );
}

function polygonBlueprint(points: PolygonPoint[], loadout: BlueprintLoadout, tier: NpcHullTier): { clip: string; body: string } {
  const CX = 240,
    CY = 250;
  const R = 175 * TIER_SCALE[tier];
  const abs = points.map((p) => rot(p));
  const px = (p: PolygonPoint) => [CX + p[0] * R, CY + p[1] * R] as PolygonPoint;
  const scaled = abs.map(px);
  const hullPath = `M${scaled.map((p) => p.join(",")).join(" L")} Z`;
  const nose = scaled[0];

  // Widest vertices (largest |x-CX|) drive hardpoint placement; rearmost
  // off-centre vertices drive nacelle placement. Both are read off the
  // real polygon, not hand-picked, so this works for any of the 11
  // polygon hulls without per-ship tuning.
  const byWidth = [...scaled].sort((a, b) => Math.abs(b[0] - CX) - Math.abs(a[0] - CX));
  const hardpointPts: PolygonPoint[] = [];
  for (const p of byWidth) {
    if (hardpointPts.length >= loadout.hardpoints) break;
    const mirror = hardpointPts.find((h) => Math.abs(h[1] - p[1]) < 6 && Math.abs(h[0] - CX + (p[0] - CX)) < 6);
    if (!mirror) hardpointPts.push(p);
  }
  const rearCandidates = scaled.filter((p) => Math.abs(p[0] - CX) > 8).sort((a, b) => b[1] - a[1]);
  const rearLeft = rearCandidates.find((p) => p[0] < CX);
  const rearRight = rearCandidates.find((p) => p[0] > CX);

  function circ(p: PolygonPoint, r: number, cls: string): string {
    return `<circle cx="${p[0]}" cy="${p[1]}" r="${r}" class="${cls}"/>`;
  }
  function line(a: PolygonPoint, b: PolygonPoint, cls: string): string {
    return `<line x1="${a[0]}" y1="${a[1]}" x2="${b[0]}" y2="${b[1]}" class="${cls}"/>`;
  }

  const rivets = scaled.map((p) => circ(p, 2, "bp-rivet")).join("");
  const spine = line([CX, nose[1]], [CX, CY + R], "bp-axis");
  const seams = scaled.map((p) => line([CX, p[1]], p, "bp-seam")).join("");

  function hardpoint(p: PolygonPoint): string {
    const w = 22,
      h = 12;
    return (
      `<g><rect x="${p[0] - w / 2}" y="${p[1] - h / 2}" width="${w}" height="${h}" rx="2" class="bp-pod"/>` +
      line([p[0] - w / 2, p[1]], [p[0] + w / 2, p[1]], "bp-pod-line") +
      circ([p[0] - w / 4, p[1]], 1.6, "bp-rivet") +
      circ([p[0] + w / 4, p[1]], 1.6, "bp-rivet") +
      "</g>"
    );
  }
  function nacelle(p: PolygonPoint): string {
    return (
      circ(p, 17, "bp-ring-outer") +
      circ(p, 11, "bp-ring-mid") +
      circ(p, 4.5, "bp-ring-core") +
      [0, 45, 90, 135]
        .map((a) => {
          const rad = (a * Math.PI) / 180;
          const dx = Math.cos(rad),
            dy = Math.sin(rad);
          return line([p[0] + 4.5 * dx, p[1] + 4.5 * dy], [p[0] + 17 * dx, p[1] + 17 * dy], "bp-vane") +
            line([p[0] - 4.5 * dx, p[1] - 4.5 * dy], [p[0] - 17 * dx, p[1] - 17 * dy], "bp-vane");
        })
        .join("")
    );
  }

  const hardpoints = loadout.hardpoints > 0 ? hardpointPts.map(hardpoint).join("") : "";
  const nacelles = [rearLeft, rearRight].filter((p): p is PolygonPoint => !!p).map(nacelle).join("");

  const canopy =
    `<ellipse cx="${CX}" cy="${nose[1] + R * 0.32}" rx="${R * 0.14}" ry="${R * 0.22}" class="bp-canopy"/>` +
    line([CX, nose[1] + R * 0.12], [CX, nose[1] + R * 0.52], "bp-canopy-line") +
    line([CX - R * 0.1, nose[1] + R * 0.22], [CX + R * 0.1, nose[1] + R * 0.22], "bp-canopy-line");

  const sensor = loadout.sensorMast ? line([CX, nose[1]], [CX, nose[1] - 16], "bp-mast") + circ([CX, nose[1] - 16], 3, "bp-mast-tip") : "";

  const miningBeam = loadout.miningBeam
    ? `<path d="M${CX - 14},${nose[1] + 6} L${CX},${nose[1] - 20} L${CX + 14},${nose[1] + 6}" class="bp-beam"/>` +
      circ([CX, nose[1] - 20], 3.5, "bp-beam-tip")
    : "";

  const cargoBay = loadout.cargoBay
    ? `<rect x="${CX - R * 0.42}" y="${CY - R * 0.05}" width="${R * 0.84}" height="${R * 0.62}" rx="8" class="bp-cargo"/>` +
      `<text x="${CX}" y="${CY + R * 0.32}" text-anchor="middle" class="bp-cargo-label">CARGO</text>`
    : "";

  const repairPorts = loadout.repairPorts
    ? scaled
        .filter((p) => Math.abs(p[0] - CX) > R * 0.55)
        .slice(0, 2)
        .map((p) => {
          const dir = p[0] > CX ? 1 : -1;
          const tip: PolygonPoint = [p[0] + dir * 20, p[1]];
          return line(p, tip, "bp-repair-beam") + circ(tip, 3, "bp-repair-tip");
        })
        .join("")
    : "";

  const hatch: string[] = [];
  for (let hy = nose[1] - 20; hy < CY + R + 20; hy += 8) {
    hatch.push(line([CX - R - 20, hy - 26], [CX - 4, hy], "bp-hatch"));
  }

  const dimLen =
    line([CX - R - 34, nose[1]], [CX - R - 34, CY + R], "bp-dim") +
    line([CX - R - 40, nose[1]], [CX - R - 28, nose[1]], "bp-dim") +
    line([CX - R - 40, CY + R], [CX - R - 28, CY + R], "bp-dim");

  const dimSpan =
    line([CX - R, CY + R + 22], [CX + R, CY + R + 22], "bp-dim") +
    line([CX - R, CY + R + 16], [CX - R, CY + R + 28], "bp-dim") +
    line([CX + R, CY + R + 16], [CX + R, CY + R + 28], "bp-dim");

  const body =
    '<style>' +
    ".bp-grid{stroke:#152030;stroke-width:1;}" +
    ".bp-axis{stroke:#3aa0e8;stroke-width:1;stroke-dasharray:9 4 2 4;opacity:0.5;}" +
    ".bp-fill{fill:rgba(58,160,232,0.045);}" +
    ".bp-outline{fill:none;stroke:#8fd1ff;stroke-width:1.8;stroke-linejoin:round;}" +
    ".bp-seam{stroke:#3aa0e8;stroke-width:0.6;opacity:0.4;}" +
    ".bp-rivet{fill:#3aa0e8;opacity:0.75;}" +
    ".bp-pod{fill:rgba(240,201,74,0.08);stroke:#f0c94a;stroke-width:1;}" +
    ".bp-pod-line{stroke:#f0c94a;stroke-width:0.6;opacity:0.7;}" +
    ".bp-ring-outer{fill:none;stroke:#3aa0e8;stroke-width:1.1;}" +
    ".bp-ring-mid{fill:none;stroke:#3aa0e8;stroke-width:0.7;opacity:0.7;}" +
    ".bp-ring-core{fill:#3aa0e8;opacity:0.5;}" +
    ".bp-vane{stroke:#3aa0e8;stroke-width:0.5;opacity:0.45;}" +
    ".bp-canopy{fill:rgba(240,201,74,0.06);stroke:#f0c94a;stroke-width:1;}" +
    ".bp-canopy-line{stroke:#f0c94a;stroke-width:0.6;opacity:0.7;}" +
    ".bp-mast{stroke:#8fd1ff;stroke-width:0.9;}" +
    ".bp-mast-tip{fill:#8fd1ff;}" +
    ".bp-beam{fill:rgba(63,174,92,0.12);stroke:#3fae5c;stroke-width:1;}" +
    ".bp-beam-tip{fill:#3fae5c;}" +
    ".bp-cargo{fill:none;stroke:#7fa8c9;stroke-width:1;stroke-dasharray:5 3;opacity:0.7;}" +
    ".bp-cargo-label{font-family:'JetBrains Mono',monospace;font-size:9px;fill:#7fa8c9;letter-spacing:0.12em;opacity:0.75;}" +
    ".bp-repair-beam{stroke:#3fae5c;stroke-width:1;opacity:0.7;}" +
    ".bp-repair-tip{fill:#3fae5c;}" +
    ".bp-hatch{stroke:#3aa0e8;stroke-width:0.45;opacity:0.22;}" +
    ".bp-dim{stroke:#5a6b7d;stroke-width:0.8;}" +
    "</style>" +
    `<g clip-path="url(#bpHullClip)"><rect x="0" y="0" width="480" height="480" class="bp-fill"/>${hatch.join("")}</g>` +
    `<path d="${hullPath}" class="bp-outline"/>` +
    spine +
    seams +
    nacelles +
    hardpoints +
    canopy +
    sensor +
    miningBeam +
    cargoBay +
    repairPorts +
    rivets +
    dimLen +
    dimSpan;

  return { clip: `<path d="${hullPath}"/>`, body };
}

function ellipseBlueprint(subtype: string, tier: NpcHullTier): { clip: string; body: string } {
  const CX = 240,
    CY = 250;
  const R = 165 * TIER_SCALE[tier];
  const ellipse = ALIEN_ELLIPSE[subtype] ?? ALIEN_ELLIPSE.Duelist;
  const rx = ellipse.side * R,
    ry = ellipse.forward * R;
  const layered = subtype === "Elite";

  function vein(angleDeg: number): string {
    const rad = (angleDeg * Math.PI) / 180;
    const x2 = CX + rx * 0.92 * Math.sin(rad);
    const y2 = CY - ry * 0.92 * Math.cos(rad);
    return `<path d="M${CX},${CY} Q${CX + rx * 0.3 * Math.sin(rad)},${CY - ry * 0.3 * Math.cos(rad)} ${x2},${y2}" class="bp-vein"/>`;
  }
  const veins = [-60, -30, 0, 30, 60, -150, 150, 180].map(vein).join("");
  const nodes = [-50, -20, 20, 50].map((a) => {
    const rad = (a * Math.PI) / 180;
    const x = CX + rx * 0.85 * Math.sin(rad);
    const y = CY - ry * 0.85 * Math.cos(rad);
    return `<circle cx="${x}" cy="${y}" r="3" class="bp-bio-node"/>`;
  }).join("");

  const outline = `<ellipse cx="${CX}" cy="${CY}" rx="${rx}" ry="${ry}" class="bp-outline"/>`;
  const inner = layered ? `<ellipse cx="${CX}" cy="${CY}" rx="${rx * 0.7}" ry="${ry * 0.7}" class="bp-alien-ring"/>` : "";
  const core = `<circle cx="${CX}" cy="${CY}" r="${R * 0.1}" class="bp-alien-core" filter="url(#bpGlow)"/>`;

  const hatch: string[] = [];
  for (let hy = CY - ry - 10; hy < CY + ry + 10; hy += 8) {
    hatch.push(`<line x1="${CX - rx - 10}" y1="${hy - 20}" x2="${CX}" y2="${hy}" class="bp-hatch"/>`);
  }

  const dimLen =
    `<line x1="${CX - rx - 34}" y1="${CY - ry}" x2="${CX - rx - 34}" y2="${CY + ry}" class="bp-dim"/>` +
    `<line x1="${CX - rx - 40}" y1="${CY - ry}" x2="${CX - rx - 28}" y2="${CY - ry}" class="bp-dim"/>` +
    `<line x1="${CX - rx - 40}" y1="${CY + ry}" x2="${CX - rx - 28}" y2="${CY + ry}" class="bp-dim"/>`;
  const dimSpan =
    `<line x1="${CX - rx}" y1="${CY + ry + 22}" x2="${CX + rx}" y2="${CY + ry + 22}" class="bp-dim"/>` +
    `<line x1="${CX - rx}" y1="${CY + ry + 16}" x2="${CX - rx}" y2="${CY + ry + 28}" class="bp-dim"/>` +
    `<line x1="${CX + rx}" y1="${CY + ry + 16}" x2="${CX + rx}" y2="${CY + ry + 28}" class="bp-dim"/>`;

  const body =
    "<style>" +
    ".bp-grid{stroke:#152030;stroke-width:1;}" +
    ".bp-fill{fill:rgba(155,89,182,0.05);}" +
    ".bp-outline{fill:none;stroke:#c9a0e0;stroke-width:1.8;}" +
    ".bp-alien-ring{fill:none;stroke:#9b59b6;stroke-width:1;opacity:0.7;}" +
    ".bp-alien-core{fill:#c9a0e0;opacity:0.75;}" +
    ".bp-vein{fill:none;stroke:#9b59b6;stroke-width:0.6;opacity:0.5;}" +
    ".bp-bio-node{fill:#c9a0e0;opacity:0.8;}" +
    ".bp-hatch{stroke:#9b59b6;stroke-width:0.45;opacity:0.2;}" +
    ".bp-dim{stroke:#5a6b7d;stroke-width:0.8;}" +
    "</style>" +
    `<g clip-path="url(#bpHullClip)"><rect x="0" y="0" width="480" height="480" class="bp-fill"/>${hatch.join("")}</g>` +
    outline +
    inner +
    veins +
    nodes +
    core +
    dimLen +
    dimSpan;

  return { clip: `<ellipse cx="${CX}" cy="${CY}" rx="${rx}" ry="${ry}"/>`, body };
}

// Resolves the drawing for any of the 13 hulls in the game. Player roles
// and Faction/Pirate polygon subtypes share one code path; Alien is a
// deliberately different (organic, no hard edges) treatment, matching
// shipCanvasIcon.ts's own ellipse-vs-polygon split.
function drawingFor(faction: BlueprintFaction, key: string, tier: NpcHullTier): { clip: string; body: string } {
  if (faction === "Alien") return ellipseBlueprint(key, tier);
  const points = faction === "Player" ? ROLE_POLYGON[key] : NPC_POLYGON[key];
  const loadout = (faction === "Player" ? PLAYER_LOADOUT[key] : NPC_LOADOUT[key]) ?? { hardpoints: 0 };
  return polygonBlueprint(points ?? ROLE_POLYGON.Journeyman, loadout, tier);
}

export function blueprintTitleFor(faction: BlueprintFaction, key: string): { subtitle: string; hue: string } {
  const subtitle = faction === "Player" ? PLAYER_BLURB[key] ?? "" : NPC_BLURB[key] ?? "";
  const hue = faction === "Player" ? PLAYER_COLOR.base : FACTION_COLOR[faction as NpcFaction].base;
  return { subtitle, hue };
}

// Full modal body: SVG drawing + spec sheet + drafting title block. The
// caller (Handbook/Trophy Room/Target Bar) owns the overlay chrome
// (open/close, backdrop) -- this only builds what goes inside it, so all
// three call sites render an identical drawing rather than three
// almost-identical ones.
export function blueprintModalHtml(spec: BlueprintSpec): string {
  const drawing = drawingFor(spec.faction, spec.key, spec.tier);
  const svg = svgEl(drawing);
  const { subtitle } = blueprintTitleFor(spec.faction, spec.key);
  const factionLabel = spec.faction === "Player" ? "Captain's fleet" : spec.faction;

  return `
    <div class="sf-bp-modal sf-panel">
      <button type="button" class="sf-bp-close" data-bp-close aria-label="Close blueprint">&times;</button>
      <div class="sf-bp-head">
        <div class="sf-bp-eyebrow">${factionLabel} &middot; ${spec.tier} &middot; TIER ${TIER_NUMERAL[spec.tier]}</div>
        <h2 class="sf-bp-title">${escapeHtml(spec.displayName)}</h2>
        <p class="sf-bp-subtitle">${escapeHtml(subtitle)}</p>
      </div>
      <div class="sf-bp-board">${svg}</div>
      <div class="sf-bp-meta">
        <table class="sf-bp-table">
          <tbody>
            ${spec.stats.map((s) => `<tr><td>${escapeHtml(s.label)}</td><td>${escapeHtml(s.value)}</td></tr>`).join("")}
          </tbody>
        </table>
        <div class="sf-bp-drawing-no">DWG ${escapeHtml(spec.drawingNo)} &middot; TOP ORTHOGRAPHIC &middot; 1:1</div>
      </div>
    </div>
  `;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

// ---- Shared overlay mount/teardown, used by all three call sites -------
let overlayEl: HTMLElement | null = null;

export function openBlueprint(spec: BlueprintSpec): void {
  closeBlueprint();
  const overlay = document.createElement("div");
  overlay.className = "sf-bp-overlay open";
  overlay.innerHTML = blueprintModalHtml(spec);
  overlay.addEventListener("click", (ev) => {
    if (ev.target === overlay || (ev.target as HTMLElement).closest("[data-bp-close]")) closeBlueprint();
  });
  document.body.appendChild(overlay);
  overlayEl = overlay;
}

export function closeBlueprint(): void {
  overlayEl?.remove();
  overlayEl = null;
}
